import { retargetClip, quatMultiply, quatInvertUnit, quatRotateVec3, conventionTriad, vec3Dot,
  type Ontology, type PriorsBundle, type RigDescriptor, type Quat } from '@harryhausen/kernel';

/** Meshy's neck segment is not a reliable face axis. Measure the actual
 * transported head at source rest, retaining the receiver's authored gaze. */
export function meshyHeadBaseline(ontology: Ontology, priors: PriorsBundle,
  source: RigDescriptor, target: RigDescriptor, profile: string): number {
  const result=retargetClip(ontology,{rig:source,clip:{
    name:'head-rest-measurement',durationSec:1/30,fps:30,additive:false,rootMotionTarget:null,
    tracks:source.bones.map(b=>({target:b.name,path:'rotation' as const,
      times:new Float32Array([0]),values:new Float32Array([b.localBind.rotation.x,b.localBind.rotation.y,b.localBind.rotation.z,b.localBind.rotation.w])})),
  }},{rig:target},{profile,priors,sourceFamily:'mixamo',targetFamily:'meshy'});
  const rotations=new Map(result.clip.tracks.filter(t=>t.path==='rotation').map(t=>[t.target,
    {x:t.values[0]!,y:t.values[1]!,z:t.values[2]!,w:t.values[3]!}]));
  const bones=new Map(target.bones.map(b=>[b.name,b]));
  const world=(name:string):Quat=>{
    const b=bones.get(name)!;
    const local=rotations.get(name)??b.localBind.rotation;
    return b.parent===null?local:quatMultiply(world(b.parent),local);
  };
  const head=bones.get(target.mapping.find(m=>m.ontologyId==='Head')!.rigBone)!;
  const delta=quatMultiply(world(head.name),quatInvertUnit(head.worldBind.rotation));
  const axes=conventionTriad(target);
  if(axes===null) throw new Error('Meshy head alignment requires a valid target convention');
  const moved=quatRotateVec3(delta,axes.up);
  const gaze=quatRotateVec3(delta,axes.facing);
  // Level delta * R(-angle) * facing, including authored yaw and roll.
  return Math.atan2(-vec3Dot(gaze,axes.up),vec3Dot(moved,axes.up))*180/Math.PI;
}
